import React                  from 'react';
import { createPortal }       from 'react-dom';
import { observer }           from 'mobx-react-lite';
import { LngLat }             from '@tokamap/core';
import { MapFraming }         from '@tokamap/core';
import { MercatorPoint }      from '@tokamap/core';
import { lngLatToMercator }   from '@tokamap/core';
import useForceUpdate         from '@widesk-core/hooks/useForceUpdate';
import currentEditorStore     from '@/TokamapEditor/stores/currentEditorStore';
import framingStore           from '@/TokamapEditor/stores/framingStore';
import useFraming             from '@/TokamapEditor/ux/Framing/useFraming';
import useDesignTokens        from '@/hooks/useDesignTokens';

const LIVE = { skipHistory: true, skipSave: true, skipConfirm: true };
const HANDLE_SIGNS: [number, number][] = [[-1, 1], [0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0]];
const ROTATE_HANDLE_OFFSET_PX = 30;
const MIN_FRAME_SIZE_PX = 24;
const ROTATION_SNAP_DEG = 3;
const TAG_INSET_PX = 10;
const TAG_EDGE_PX = 8;
const TAG_TOP_SAFE_PX = 56;

type Point = { x: number; y: number };

function axes(rotationDeg: number) {
	const bearing = (-rotationDeg * Math.PI) / 180;

	return {
		right: [Math.cos(bearing), -Math.sin(bearing)] as MercatorPoint,
		up: [Math.sin(bearing), Math.cos(bearing)] as MercatorPoint,
	};
}

const dot = (a: MercatorPoint, b: MercatorPoint) => a[0] * b[0] + a[1] * b[1];

type Rect = { center: MercatorPoint; width: number; height: number };
type LocalBounds = { minA: number; maxA: number; minB: number; maxB: number };

function localBounds(rect: Rect, rotationDeg: number): LocalBounds {
	const { right, up } = axes(rotationDeg);
	const a = dot(rect.center, right);
	const b = dot(rect.center, up);

	return { minA: a - rect.width / 2, maxA: a + rect.width / 2, minB: b - rect.height / 2, maxB: b + rect.height / 2 };
}

function fromLocalBounds(bounds: LocalBounds, rotationDeg: number): Rect {
	const { right, up } = axes(rotationDeg);
	const a = (bounds.minA + bounds.maxA) / 2;
	const b = (bounds.minB + bounds.maxB) / 2;

	return {
		center: [right[0] * a + up[0] * b, right[1] * a + up[1] * b],
		width: bounds.maxA - bounds.minA,
		height: bounds.maxB - bounds.minB,
	};
}

function containing(outer: Rect, inner: Rect, rotationDeg: number): Rect {
	const o = localBounds(outer, rotationDeg);
	const i = localBounds(inner, rotationDeg);

	return fromLocalBounds({
		minA: Math.min(o.minA, i.minA),
		maxA: Math.max(o.maxA, i.maxA),
		minB: Math.min(o.minB, i.minB),
		maxB: Math.max(o.maxB, i.maxB),
	}, rotationDeg);
}

function clippedInside(inner: Rect, outer: Rect, rotationDeg: number): Rect {
	const o = localBounds(outer, rotationDeg);
	const i = localBounds(inner, rotationDeg);

	return fromLocalBounds({
		minA: Math.max(o.minA, i.minA),
		maxA: Math.min(o.maxA, i.maxA),
		minB: Math.max(o.minB, i.minB),
		maxB: Math.min(o.maxB, i.maxB),
	}, rotationDeg);
}

function shiftedInside(inner: Rect, outer: Rect, rotationDeg: number): Rect {
	const o = localBounds(outer, rotationDeg);
	const i = localBounds(inner, rotationDeg);
	const shift = (min: number, max: number, outerMin: number, outerMax: number) => {
		if (max - min > outerMax - outerMin) return (outerMin + outerMax) / 2 - (min + max) / 2;
		if (min < outerMin) return outerMin - min;
		if (max > outerMax) return outerMax - max;
		return 0;
	};
	const da = shift(i.minA, i.maxA, o.minA, o.maxA);
	const db = shift(i.minB, i.maxB, o.minB, o.maxB);

	return fromLocalBounds({ minA: i.minA + da, maxA: i.maxA + da, minB: i.minB + db, maxB: i.maxB + db }, rotationDeg);
}

function resizedRect(
	rect: { center: MercatorPoint; width: number; height: number },
	rotationDeg: number,
	signs: [number, number],
	pointer: MercatorPoint,
	minSize: number,
) {
	const { right, up } = axes(rotationDeg);
	const [sa, sb] = signs;
	const ca = dot(rect.center, right);
	const cb = dot(rect.center, up);
	const anchorA = ca - (sa * rect.width) / 2;
	const anchorB = cb - (sb * rect.height) / 2;
	const width = sa ? Math.max(minSize, (dot(pointer, right) - anchorA) * sa) : rect.width;
	const height = sb ? Math.max(minSize, (dot(pointer, up) - anchorB) * sb) : rect.height;
	const nextA = sa ? anchorA + (sa * width) / 2 : ca;
	const nextB = sb ? anchorB + (sb * height) / 2 : cb;

	return {
		center: [right[0] * nextA + up[0] * nextB, right[1] * nextA + up[1] * nextB] as MercatorPoint,
		width,
		height,
	};
}

const FramingOverlay = observer(() => {
	const editor = currentEditorStore.get;
	const container = editor?.mapAdapter.getContainerElement();

	if (!editor || !container || !framingStore.editing || framingStore.question === null) return null;

	return createPortal(<FramingOverlayContent container={container} />, container);
});

export default FramingOverlay;

const FramingOverlayContent = observer(({ container }: { container: HTMLElement; }) => {
	const tokens = useDesignTokens();
	const editor = currentEditorStore.get;
	const adapter = editor.mapAdapter;
	const forceUpdate = useForceUpdate();
	const { framing, geometry } = useFraming();
	const question = framingStore.question;

	React.useEffect(() => adapter.onCameraMove(forceUpdate), [adapter]);

	const width = container.clientWidth;
	const height = container.clientHeight;
	const project = (lngLat: LngLat): Point => adapter.projectToScreen(lngLat);
	const pointerToMercator = (event: MouseEvent | React.MouseEvent): MercatorPoint => {
		const bounds = container.getBoundingClientRect();
		return lngLatToMercator(adapter.screenToLngLat(event.clientX - bounds.left, event.clientY - bounds.top));
	};
	const metersPerPixel = () => {
		const a = pointerlessMercator(0, 0);
		const b = pointerlessMercator(100, 0);
		return Math.hypot(b[0] - a[0], b[1] - a[1]) / 100;
	};
	const pointerlessMercator = (x: number, y: number) => lngLatToMercator(adapter.screenToLngLat(x, y));

	const startDrag = (
		event: MouseEvent | React.MouseEvent,
		onMove: (pointer: MercatorPoint, from: MapFraming) => MapFraming,
		onEnd?: (framing: MapFraming) => void,
	) => {
		event.preventDefault();
		event.stopPropagation();

		const from = framing;
		let last = from;

		const move = (moveEvent: MouseEvent) => {
			last = onMove(pointerToMercator(moveEvent), from);
			editor.setFraming(last, LIVE);
		};

		const end = () => {
			window.removeEventListener('mousemove', move);
			window.removeEventListener('mouseup', end);
			if (last === from) return;

			editor.setFraming(last);
			onEnd?.(last);
		};

		window.addEventListener('mousemove', move);
		window.addEventListener('mouseup', end);
	};

	const startPolygon = geometry.start?.map(project);
	const limitPolygon = geometry.limit?.map(project);
	const toPath = (points: Point[]) => {
		const clipped = clipToViewport(points, width, height);
		return clipped.length ? `M${clipped.map(p => `${p.x},${p.y}`).join('L')}Z` : '';
	};
	const outerPath = `M-10,-10L${width + 10},-10L${width + 10},${height + 10}L-10,${height + 10}Z`;

	const startRect = geometry.startRect;
	const limitRect = geometry.limitRect;

	const keepInsideLimit = (rect: Rect, from: MapFraming, fit: (inner: Rect, outer: Rect, rotationDeg: number) => Rect) => (
		from.limit.mode === 'frame' && limitRect ? fit(rect, limitRect, from.rotationDeg) : rect
	);
	const editStart = question === 'start' && framing.start.mode === 'frame' && !!startRect;
	const editLimit = question === 'limit' && framing.limit.mode === 'frame';
	const showRotation = question === 'start' && !!startPolygon;

	const startHandles = editStart && startPolygon ? HANDLE_SIGNS.map(signs => ({
		signs,
		point: handlePoint(startPolygon, signs),
	})) : [];

	const limitHandles = editLimit && limitPolygon ? HANDLE_SIGNS.map(signs => ({
		signs,
		point: handlePoint(limitPolygon, signs),
	})) : [];

	const topMid = startPolygon ? midpoint(startPolygon[0]!, startPolygon[1]!) : null;
	const center = startPolygon ? midpoint(startPolygon[0]!, startPolygon[2]!) : null;
	const rotateHandle = topMid && center ? offsetAway(topMid, center, ROTATE_HANDLE_OFFSET_PX) : null;

	const startMove = (event: MouseEvent | React.MouseEvent) => {
		const origin = pointerToMercator(event);

		startDrag(event, (pointer, from) => {
			if (from.start.mode !== 'frame') return from;

			const { frame } = from.start;

			return {
				...from,
				start: {
					mode: 'frame',
					frame: keepInsideLimit({
						...frame,
						center: [frame.center[0] + pointer[0] - origin[0], frame.center[1] + pointer[1] - origin[1]],
					}, from, shiftedInside),
				},
			};
		});
	};

	const startColor = tokens.color.primary;
	const limitColor = tokens.text.secondary;

	return (
		<div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none', overflow: 'hidden' }}>
			<svg width={width} height={height} style={{ position: 'absolute', inset: 0 }}>
				{question === 'limit' && limitPolygon && (
					<path d={`${outerPath}${toPath(limitPolygon)}`} fillRule="evenodd" style={{ fill: `color-mix(in srgb, ${tokens.text.default} 35%, transparent)` }} />
				)}

				{question === 'start' && startPolygon && (
					<path d={`${outerPath}${toPath(startPolygon)}`} fillRule="evenodd" style={{ fill: `color-mix(in srgb, ${tokens.text.default} 18%, transparent)` }} />
				)}

				{limitPolygon && (
					<path
						d={toPath(limitPolygon)}
						fill="none"
						stroke={limitColor}
						strokeWidth={2.5}
						strokeDasharray="8 6"
						opacity={question === 'limit' ? 1 : 0.5}
					/>
				)}

				{startPolygon && (
					<path
						d={toPath(startPolygon)}
						fill="none"
						stroke={startColor}
						strokeWidth={3}
						opacity={question === 'start' || question === null ? 1 : 0.5}
					/>
				)}

				{showRotation && topMid && rotateHandle && (
					<line x1={topMid.x} y1={topMid.y} x2={rotateHandle.x} y2={rotateHandle.y} stroke={startColor} strokeWidth={2} />
				)}
			</svg>

			{startPolygon && (
				<Tag polygon={startPolygon} corner="bottomLeft" bounds={{ width, height }} color={startColor} number={1} faded={question !== 'start' && question !== null}>
					Ce qu'on voit en arrivant
				</Tag>
			)}

			{limitPolygon && (
				<Tag polygon={limitPolygon} corner="bottomRight" bounds={{ width, height }} color={limitColor} number={2} faded={question !== 'limit'}>
					Jusqu'où on peut aller
				</Tag>
			)}

			{editStart && center && (
				<MoveHandle point={center} color={startColor} onMouseDown={startMove} />
			)}

			{startHandles.map(({ signs, point }) => (
				<Handle
					key={signs.join()}
					point={point}
					color={startColor}
					onMouseDown={event => startDrag(event, (pointer, from) => ({
						...from,
						start: {
							mode: 'frame',
							frame: keepInsideLimit(
								resizedRect(startRect!, from.rotationDeg, signs, pointer, MIN_FRAME_SIZE_PX * metersPerPixel()),
								from,
								clippedInside,
							),
						},
					}))}
				/>
			))}

			{limitHandles.map(({ signs, point }) => (
				<Handle
					key={signs.join()}
					point={point}
					color={limitColor}
					onMouseDown={event => startDrag(event, (pointer, from) => {
						if (from.limit.mode !== 'frame' || !limitRect) return from;

						return {
							...from,
							limit: {
								mode: 'frame',
								frame: startRect
									? containing(resizedRect(limitRect, from.rotationDeg, signs, pointer, MIN_FRAME_SIZE_PX * metersPerPixel()), startRect, from.rotationDeg)
									: resizedRect(limitRect, from.rotationDeg, signs, pointer, MIN_FRAME_SIZE_PX * metersPerPixel()),
							},
						};
					})}
				/>
			))}

			{showRotation && rotateHandle && startRect && (
				<>
					<Handle
						round
						point={rotateHandle}
						color={startColor}
						onMouseDown={event => startDrag(event, (pointer, from) => {
							const vector: MercatorPoint = [pointer[0] - startRect.center[0], pointer[1] - startRect.center[1]];
							const bearing = (Math.atan2(vector[0], vector[1]) * 180) / Math.PI;
							const rotation = Math.round(-bearing);
							const snapped = Math.abs(rotation) <= ROTATION_SNAP_DEG ? 0 : rotation;

							return { ...from, rotationDeg: ((snapped + 540) % 360) - 180 };
						}, framing => adapter.animateToView({ rotationDeg: framing.rotationDeg }))}
					/>

					<div
						style={{
							position: 'absolute',
							left: rotateHandle.x + 14,
							top: rotateHandle.y - 11,
							padding: '2px 7px',
							borderRadius: 4,
							background: startColor,
							color: tokens.surface.card,
							fontSize: 11.5,
							fontWeight: 600,
							whiteSpace: 'nowrap',
						}}
					>
						{framing.rotationDeg ? `${Math.round(framing.rotationDeg)}°` : 'Nord'}
					</div>
				</>
			)}
		</div>
	);
});

const CLIP_MARGIN_PX = 40;

function clipToViewport(polygon: Point[], width: number, height: number): Point[] {
	const edges: [(p: Point) => boolean, (a: Point, b: Point) => Point][] = [
		[p => p.x >= -CLIP_MARGIN_PX, (a, b) => intersectX(a, b, -CLIP_MARGIN_PX)],
		[p => p.x <= width + CLIP_MARGIN_PX, (a, b) => intersectX(a, b, width + CLIP_MARGIN_PX)],
		[p => p.y >= -CLIP_MARGIN_PX, (a, b) => intersectY(a, b, -CLIP_MARGIN_PX)],
		[p => p.y <= height + CLIP_MARGIN_PX, (a, b) => intersectY(a, b, height + CLIP_MARGIN_PX)],
	];

	return edges.reduce((points, [inside, intersect]) => {
		const result: Point[] = [];

		points.forEach((current, index) => {
			const previous = points[(index + points.length - 1) % points.length]!;

			if (inside(current)) {
				if (!inside(previous)) result.push(intersect(previous, current));
				result.push(current);
			} else if (inside(previous)) {
				result.push(intersect(previous, current));
			}
		});

		return result;
	}, polygon);
}

function intersectX(a: Point, b: Point, x: number): Point {
	return { x, y: a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x) };
}

function intersectY(a: Point, b: Point, y: number): Point {
	return { x: a.x + ((b.x - a.x) * (y - a.y)) / (b.y - a.y), y };
}

function midpoint(a: Point, b: Point): Point {
	return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function offsetAway(from: Point, center: Point, distance: number): Point {
	const dx = from.x - center.x;
	const dy = from.y - center.y;
	const length = Math.hypot(dx, dy) || 1;

	return { x: from.x + (dx / length) * distance, y: from.y + (dy / length) * distance };
}

function handlePoint(polygon: Point[], [sa, sb]: [number, number]): Point {
	const [topLeft, topRight, bottomRight, bottomLeftPoint] = polygon as [Point, Point, Point, Point];
	const top = sa < 0 ? topLeft : sa > 0 ? topRight : midpoint(topLeft, topRight);
	const bottom = sa < 0 ? bottomLeftPoint : sa > 0 ? bottomRight : midpoint(bottomLeftPoint, bottomRight);

	return sb > 0 ? top : sb < 0 ? bottom : midpoint(top, bottom);
}

function Handle({ point, color, round, onMouseDown }: {
	point: Point;
	color: string;
	round?: boolean;
	onMouseDown: (event: React.MouseEvent) => void;
}) {
	const tokens = useDesignTokens();
	const size = round ? 18 : 12;

	return (
		<div
			data-framing-control
			onMouseDown={onMouseDown}
			style={{
				position: 'absolute',
				left: point.x - size / 2,
				top: point.y - size / 2,
				width: size,
				height: size,
				boxSizing: 'border-box',
				borderRadius: round ? '50%' : 2,
				border: `2px solid ${color}`,
				background: tokens.surface.card,
				cursor: round ? 'grab' : 'move',
				pointerEvents: 'auto',
			}}
		/>
	);
}

function MoveHandle({ point, color, onMouseDown }: {
	point: Point;
	color: string;
	onMouseDown: (event: React.MouseEvent) => void;
}) {
	const tokens = useDesignTokens();

	return (
		<div
			data-framing-control
			title="Déplacer le cadre"
			onMouseDown={onMouseDown}
			style={{
				position: 'absolute',
				left: point.x - 16,
				top: point.y - 16,
				width: 32,
				height: 32,
				boxSizing: 'border-box',
				borderRadius: '50%',
				border: `2px solid ${color}`,
				background: tokens.surface.card,
				color,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				cursor: 'move',
				pointerEvents: 'auto',
				boxShadow: tokens.shadow.card,
			}}
		>
			<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
				<path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3" />
			</svg>
		</div>
	);
}

function Tag({ polygon, corner, bounds, color, number, faded, children }: {
	polygon: Point[];
	corner: 'bottomLeft' | 'bottomRight';
	bounds: { width: number; height: number };
	color: string;
	number: number;
	faded?: boolean;
	children: React.ReactNode;
}) {
	const tokens = useDesignTokens();
	const ref = React.useRef<HTMLDivElement>(null);
	const [size, setSize] = React.useState({ width: 180, height: 24 });

	React.useLayoutEffect(() => {
		const element = ref.current;

		if (element && (element.offsetWidth !== size.width || element.offsetHeight !== size.height)) {
			setSize({ width: element.offsetWidth, height: element.offsetHeight });
		}
	});

	const anchor = offsetAway(corner === 'bottomLeft' ? polygon[3]! : polygon[2]!, midpoint(polygon[0]!, polygon[2]!), -TAG_INSET_PX);
	const left = corner === 'bottomLeft' ? anchor.x : anchor.x - size.width;
	const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

	return (
		<div
			ref={ref}
			style={{
				position: 'absolute',
				left: clamp(left, TAG_EDGE_PX, bounds.width - size.width - TAG_EDGE_PX),
				top: clamp(anchor.y - size.height, TAG_TOP_SAFE_PX, bounds.height - size.height - TAG_EDGE_PX),
				opacity: faded ? 0.5 : 1,
				display: 'flex',
				alignItems: 'center',
				gap: 6,
				padding: '3px 9px 3px 3px',
				borderRadius: 4,
				background: color,
				color: tokens.surface.card,
				fontSize: 12,
				fontWeight: 600,
				whiteSpace: 'nowrap',
			}}
		>
			<span
				style={{
					width: 18,
					height: 18,
					borderRadius: '50%',
					background: tokens.surface.card,
					color,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: 11,
				}}
			>
				{number}
			</span>
			{children}
		</div>
	);
}
